import { Link } from "react-router-dom";
import { C } from "../constants";
import { Sec, STitle, ContentBlock } from "../components/Shared";

export default function Compliance() {
  return (
    <div>
      <Sec>
        <STitle tag="Compliance" title="How We Protect Our Users" sub="SafeZapp is designed for people who need an approver. Because of that, we hold ourselves to the highest standard of data protection — particularly when it comes to the privacy of vulnerable users." />

        <div style={{ maxWidth: 740, margin: "0 auto" }}>
          <ContentBlock icon="✋" title="Nothing happens without consent">
            <p style={{ margin: "0 0 10px" }}>We only complete account setup once a responsible approver has accepted the account. If an approver is required, we temporarily store only the minimum details needed for them to identify and link to the account.</p>
            <p style={{ margin: 0 }}>If no approver connects within 14 days, that information is automatically and permanently deleted.</p>
          </ContentBlock>

          <ContentBlock icon="🔗" title="Approvers are involved from the start">
            <p style={{ margin: 0 }}>We use a pairing code system. The approver generates a unique 6-digit code, and the person they're responsible for enters it during signup. This means both parties must actively participate — no one can be linked without direct, real-world coordination between them.</p>
          </ContentBlock>

          <ContentBlock icon="✅" title="We don't guess — approvers confirm">
            <p style={{ margin: 0 }}>When an approver accepts a link, they confirm the age bracket of the person they're responsible for and declare their relationship. We rely on the approver's attestation rather than asking users to provide personal details about themselves.</p>
          </ContentBlock>

          <ContentBlock icon="📦" title="We only collect what we need, when we need it">
            <p style={{ margin: 0 }}>We don't register devices for push notifications until an approver relationship is established. We don't collect dates of birth. We don't build profiles. The data we hold is limited to what's necessary for the app to function, and nothing more.</p>
          </ContentBlock>

          <ContentBlock icon="🎛️" title="Your data, your control">
            <p style={{ margin: 0 }}>Our full privacy policy is available <a href="#" style={{ color: C.blue, fontWeight: 600 }}>here</a> and provided at sign-up. We minimise what we collect to only what's necessary to operate the app, and we automatically remove data in line with defined retention periods.</p>
          </ContentBlock>

          <div style={{
            background: `linear-gradient(135deg, ${C.blueLight}, ${C.bluePale})`,
            borderRadius: 16, padding: "28px 32px", marginTop: 8, marginBottom: 20,
            border: `1px solid ${C.border}`
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
              <span style={{ fontSize: 22 }}>💡</span>
              <h3 style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: 18, color: C.blueDark, margin: 0 }}>A note on why we built it this way</h3>
            </div>
            <p style={{ fontFamily: "'Nunito', sans-serif", color: C.textMid, fontSize: 15, lineHeight: 1.75, margin: 0 }}>
              Many apps collect data first and ask questions later. We chose to do the opposite. Every technical decision — from deferred account creation to automatic deletion of unlinked accounts — exists because we believe consent should come before data collection, not after. These aren't just policies written on a page. <strong style={{ color: C.text }}>They're built into how the app works.</strong>
            </p>
          </div>
        </div>
      </Sec>

      {/* Technical compliance details */}
      <Sec bg={C.bg}>
        <STitle title="Compliance Standards" sub="The frameworks and regulations that guide how we operate" />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(310px, 1fr))", gap: 22 }}>
          {[
            { t: "GDPR Compliant", i: "🇪🇺", c: "Full compliance with the General Data Protection Regulation. Lawful basis for processing, data minimisation, and clear data subject rights including the right to erasure." },
            { t: "UK Children's Code", i: "🇬🇧", c: "Designed in accordance with the Age Appropriate Design Code. High privacy settings by default, data minimisation for all users, and transparency appropriate to each user's needs." },
            { t: "COPPA Compliant", i: "🇺🇸", c: "Compliant with the Children's Online Privacy Protection Act. Verifiable approver consent before collection of personal information." },
            { t: "AI & Content Filtering", i: "🤖", c: "SafeZapp uses AI to encourage kind communication. When a message might be hurtful, the app suggests a better way to say it. This process is fully automated — no human ever reads any messages." },
            { t: "Encryption", i: "🔐", c: "Your data is protected with strong encryption when it's sent and when it's stored." },
            { t: "Audit Trail", i: "📋", c: "We keep limited records of key account actions to meet compliance requirements, and only for as long as necessary." },
          ].map((s, i) => (
            <div key={i} style={{ background: C.white, borderRadius: 14, padding: 26, border: `1px solid ${C.border}` }}>
              <span style={{ fontSize: 22, display: "block", marginBottom: 10 }}>{s.i}</span>
              <h3 style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: 16, color: C.text, margin: "0 0 6px" }}>{s.t}</h3>
              <p style={{ fontFamily: "'Nunito', sans-serif", color: C.textMid, fontSize: 14, lineHeight: 1.7, margin: 0 }}>{s.c}</p>
            </div>
          ))}
        </div>
      </Sec>

      {/* Legal links */}
      <Sec>
        <div style={{ padding: 28, background: C.blueLight, borderRadius: 14, textAlign: "center" }}>
          <p style={{ fontFamily: "'Nunito', sans-serif", fontSize: 14.5, color: C.text, margin: 0 }}>
            For full details, see our{" "}
            <Link to="/privacy" style={{ color: C.blue, fontWeight: 700, textDecoration: "none" }}>Privacy Policy</Link>,{" "}
            <Link to="/terms" style={{ color: C.blue, fontWeight: 700, textDecoration: "none" }}>Terms of Service</Link>, and{" "}
            <a href="#" style={{ color: C.blue, fontWeight: 700 }}>Cookie Policy</a>.
            {" "}These documents are also accessible within the app at all times.
          </p>
        </div>
      </Sec>
    </div>
  );
}
